import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useUser } from "../../context/UserContext"; // Import useUser
import { useTheme } from "../../context/ThemeContext";
import { Moon, Sun } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token or param meta tag not found!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

export const Header = () => {
    const { user, loading, setUser } = useUser(); // Use user, loading, and setUser from context
    const navigate = useNavigate();
    const { theme, setTheme } = useTheme();

    const handleLogout = async () => {
        try {
            const { token } = getCsrfToken();
            if (!token) {
                console.error("CSRF token not found.");
                return;
            }

            const response = await fetch("/api/logout", {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": token,
                },
            });
            if (response.ok) {
                setUser(null); // Update context after logout
                navigate("/");
            } else {
                console.error("Failed to logout");
            }
        } catch (error) {
            console.error("Error during logout:", error);
        }
    };

    const capitalize = (s) => {
        if (typeof s !== "string") return "";
        return s
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    return (
        <header className="bg-card p-4 shadow-md sticky top-0 z-50">
            <nav className="container mx-auto flex justify-between items-center">
                <Link to="/" className="text-2xl font-bold text-primary">
                    JátékFordító
                </Link>
                <div className="flex items-center gap-4">
                    <Link to="/games">
                        <Button variant="ghost">Játékok</Button>
                    </Link>

                    {user && ( // Only show settings if user is logged in
                        <Link to="/settings">
                            <Button variant="ghost">Beállítások</Button>
                        </Link>
                    )}

                    {user && user.admin && ( // Only show Admin if user is logged in and is an admin
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost">Admin</Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => navigate("/adminPanel/gemini-apikey")}>
                                    Gemini APIKEY
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate("/admin/users")}>
                                    Felhasználók
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}

                    <Button
                        variant="outline"
                        onClick={() => {
                            const themes = ["light", "dark", "dark-temp", "weblate"];
                            const currentThemeIndex = themes.indexOf(theme);
                            const nextTheme = themes[(currentThemeIndex + 1) % themes.length];
                            setTheme(nextTheme as "light" | "dark" | "dark-temp" | "weblate");
                        }}
                        className="w-32"
                    >
                        <div className="flex items-center gap-2">
                            <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                            <Moon className="h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                            <span className="transition-all">{capitalize(theme)}</span>
                        </div>
                    </Button>

                    {loading ? (
                        <div>Loading...</div>
                    ) : user ? (
                        <div className="flex items-center gap-2">
                            <Button variant="outline" onClick={handleLogout}>
                                Kilépés
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Link to="/login">
                                <Button variant="outline">Belépés</Button>
                            </Link>
                            <Link to="/register">
                                <Button>Regisztráció</Button>
                            </Link>
                        </div>
                    )}
                </div>
            </nav>
        </header>
    );
};